"use client";

import { useMemo, useState } from "react";
import type { EconomicEvent } from "@/vybe-trader-client/economic-calendar/types";
import { cn } from "@/lib/utils";
import { Input } from "@/shadcn-components/ui/input";
import {
  buildSeriesCatalogRows,
  type EconomicSeriesCatalogRow,
} from "@/vybe-trader-client/economic-calendar/catalog-rows";
import type { ImpactLevel } from "./impact-styles";
import {
  impactBadgeClassName,
  impactLucideIcons,
} from "./impact-styles";
import { currencyPairLabel } from "./currency-flag-emoji";
import { eventMatchesSearch } from "./selectors";

const ROW_GRID = "72px 84px minmax(0,1fr) 36px";

interface EconomicCalendarCatalogListProps {
  allEvents: EconomicEvent[];
  onSeriesRowClick: (compositeKey: string) => void;
}

function rowMatchesQuery(row: EconomicSeriesCatalogRow, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  const synthetic: EconomicEvent = {
    timeLabel: "",
    impact: row.impact,
    actual: "",
    forecast: "",
    previous: "",
    name: row.name,
    currency: row.currency,
    date: "",
    dateTimeISO: "",
  };
  return (
    eventMatchesSearch(synthetic, q) ||
    String(row.impact).toLowerCase().includes(t)
  );
}

export function EconomicCalendarCatalogList({
  allEvents,
  onSeriesRowClick,
}: EconomicCalendarCatalogListProps) {
  const [query, setQuery] = useState("");
  const rows = useMemo(
    () => buildSeriesCatalogRows(allEvents),
    [allEvents]
  );
  const filtered = useMemo(
    () => rows.filter((r) => rowMatchesQuery(r, query)),
    [rows, query]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border">
      <div className="border-b border-border px-2 py-1">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search impact, currency, event…"
          className="h-8 bg-background text-xs"
          aria-label="Catalog search"
        />
      </div>
      <div className="shrink-0 border-b border-border bg-muted/30 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Catalog (impact · CCY · event · count)
      </div>
      <div
        className="grid shrink-0 border-b border-border bg-muted/30 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
        style={{ gridTemplateColumns: ROW_GRID }}
      >
        <HCell>Impact</HCell>
        <HCell align="center">Ccy</HCell>
        <HCell>Event</HCell>
        <HCell align="center">#</HCell>
      </div>
      <div className="min-h-0 flex-1 overflow-auto overflow-x-hidden">
        {filtered.length === 0 ? (
          <div className="px-2 py-4 text-center text-[11px] text-muted-foreground">
            No matching series.
          </div>
        ) : (
          filtered.map((r) => {
            const imp = r.impact as ImpactLevel;
            const Icon = impactLucideIcons[imp];
            const pair = currencyPairLabel(r.currency);
            return (
              <button
                key={r.compositeKey}
                type="button"
                className="grid w-full items-center gap-px border-b border-border text-left text-[10px] leading-tight hover:bg-muted/25 focus-visible:bg-muted/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                style={{ gridTemplateColumns: ROW_GRID }}
                onClick={() => onSeriesRowClick(r.compositeKey)}
              >
                <div className="flex min-w-0 px-1">
                  <span
                    className={cn(
                      "inline-flex max-w-full items-center gap-0.5 truncate rounded border px-1 py-px text-[9px]",
                      impactBadgeClassName(imp)
                    )}
                  >
                    <Icon className="h-2.5 w-2.5 shrink-0" />
                    <span className="truncate">{imp}</span>
                  </span>
                </div>
                <div className="flex items-center justify-center gap-1 px-1 font-mono">
                  <span
                    className="select-none text-[13px] leading-none"
                    title={pair.codeUpper}
                    aria-hidden
                  >
                    {pair.flag}
                  </span>
                  <span>{pair.codeUpper}</span>
                </div>
                <div className="min-w-0 truncate px-1" title={r.name}>
                  {r.name}
                </div>
                <div className="px-1 text-center font-mono tabular-nums text-foreground">
                  {r.count}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function HCell({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: "center";
}) {
  return (
    <div
      className={cn(
        "truncate px-1 py-1",
        align === "center" && "text-center"
      )}
    >
      {children}
    </div>
  );
}
